import React, { useState } from "react";

/**
 * PipelineTabs — shared dispoline-style tab bar.
 *
 * Mirrors the dispoline-monorepo DispositionPage.jsx tab pattern:
 *   border-b underline with brand-pink active border, lucide icon + label,
 *   optional count badge.
 *
 * Two modes:
 *   1. Controlled  : <PipelineTabs activeKey="x" onSelect={fn}>{<Tab .../>}</PipelineTabs>
 *   2. Uncontrolled: <PipelineTabs defaultActiveKey="x" onTabChange={fn}>{<Tab .../>}</PipelineTabs>
 *
 * Each child should be a <Tab> with props:
 *   - eventKey: string (unique tab id)
 *   - label:    string (tab label)              [preferred]
 *   - title:    string | ReactNode (legacy, alternative to label)
 *   - icon:     lucide Icon component           [optional]
 *   - count:    number                          [optional badge]
 *   - children: ReactNode                       (tab panel content)
 */
export const Tab = ({ children }) => <>{children}</>;

const PipelineTabs = ({
  children,
  activeKey: controlledKey,
  onSelect,
  defaultActiveKey,
  onTabChange,
  className = "",
}) => {
  const isControlled = controlledKey !== undefined;
  const tabs = React.Children.toArray(children).filter(Boolean);
  const [internalKey, setInternalKey] = useState(
    defaultActiveKey ?? tabs[0]?.props?.eventKey
  );
  const activeKey = isControlled ? controlledKey : internalKey;

  const handleClick = (key) => {
    if (!isControlled) setInternalKey(key);
    if (onSelect) onSelect(key);
    if (onTabChange) onTabChange(key);
  };

  return (
    <div className={`w-full flex flex-col ${className}`}>
      <div className="border-b border-gray-200 flex-shrink-0">
        <nav className="-mb-px flex gap-1 overflow-x-auto" aria-label="Tabs">
          {tabs.map((child) => {
            const { eventKey, label, title, icon: Icon, count } = child.props;
            const isActive = activeKey === eventKey;
            return (
              <button
                key={eventKey}
                onClick={() => handleClick(eventKey)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "border-brand-pink text-brand-pink"
                    : "border-transparent text-muted-foreground hover:border-gray-300 hover:text-foreground"
                }`}
              >
                {Icon && <Icon size={15} />}
                {label ?? title}
                {count !== undefined && count !== null && (
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${
                      isActive
                        ? "bg-brand-pink text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 min-h-0 mt-4">
        {tabs.map((child) =>
          activeKey === child.props.eventKey ? (
            <div key={child.props.eventKey} className="h-full">
              {child.props.children}
            </div>
          ) : null
        )}
      </div>
    </div>
  );
};

export default PipelineTabs;
